import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { push } from "connected-react-router";
import classNames from "classnames";
import queryString from "query-string";
import Routes from "../../routes";
import {
  signalRouteChange,
  signalBackButtonPress,
  updateAppLocation,
  activateGoogleOptimize,
} from "../../actions/AppActions";
import { getDeviceBrowser, togglePageScrollability } from "../../utils/general";

// Components
import LoadingSpinner from "../Atoms/LoadingSpinner";
import Ribbon from "../Atoms/Ribbon";
// import CountdownRibbon from '../Molecules/CountdownRibbon'
import Cart from "../Organisms/Cart";
import Footer from "../Organisms/Footer";
// import Navigation from '../Organisms/Navigation'
import SuperNavigation from "../Organisms/SuperNavigation";
import { TrackingProvider } from "../Utils/TrackingWrapper";
import Constants from "../../utils/constants";
import { isNewCartEnabled, slugify } from "../../utils/general";
import WhyToBuy from "../Organisms/RestoreProductPage/WhyToBuy";

// Constants
// const HIDE_NAV_FROM_PAGES = [
//   '/linkedin-wellness'
// ]
const ENV_VAR_PATH_DELIMITER = "|";

const WHY_BUY_WHITELIST = [
  "/restore",
  "/rest-mini",
  "/rest",
  "/restsecondgen",
  "/rest-plus",
  "/dream-shop/hatch-cloud-pillow",
];

export function __constructPathsFromEnvVars(paths = null) {
  if (!paths) {
    return [];
  }

  return paths.split(ENV_VAR_PATH_DELIMITER).map((path) => `/${path}`);
}

// Load Zendesk Live Chat Widget
// function zendeskLiveChatWidgetHelper() {
//   //get pst hour and day
//   const pstNow = new Date().toLocaleString('en-US', {
//     timeZone: 'America/Los_Angeles',
//   });
//   let pstHour = new Date(pstNow).getHours();
//   let pstDay = new Date(pstNow).getDay();
//
//   //check to see if zendesk widget exists on the page
//   let zendeskIframeEl = document.querySelector('div[role=presentation] iframe[title="Button to launch messaging window"]');
//   // if Monday - Friday
//   if (pstDay > 0 && pstDay < 6) {
//     // if from 9:00amPST - 4:00pmPST
//     if (pstHour >= 9 && pstHour < 16) {
//       // only use Zendesk widget script on the restore page
//       if (document.location.pathname === '/restore') {
//         //add Zendesk Script
//         const scriptTag = document.createElement("script");
//         scriptTag.id = "ze-snippet"
//         scriptTag.src = "https://static.zdassets.com/ekr/snippet.js?key=a2f918e3-de4d-4926-90af-bd3586ca6d60";
//         scriptTag.async = true;
//         document.body.appendChild(scriptTag);
//
//         //if zendesk is hidden, unhide it
//         if (zendeskIframeEl) zendeskIframeEl.style.display = 'unset';
//       } else {
//         // if we aren't on the restore page and zendesk chat widget exists, hide it
//         if (window.zE) {
//           window.zE('messenger', 'close');
//         }
//
//         if (zendeskIframeEl) zendeskIframeEl.style.display = 'none';
//       }
//     }
//   }
// }

class App extends Component {
  static propTypes = {
    location: PropTypes.object.isRequired,
    rehydrationComplete: PropTypes.bool,
    signalRouteChange: PropTypes.func,
    signalBackButtonPress: PropTypes.func,
    errors: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.arrayOf(PropTypes.object),
    ]),
    appLocation: PropTypes.object,
    history: PropTypes.object.isRequired,
    push: PropTypes.func.isRequired,
    updateAppLocation: PropTypes.func.isRequired,
    activateGoogleOptimize: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      deviceBrowserClass: null,
    };
  }

  _sanitizeUrl = (location = this.props.location) => {
    // Use react router's location prop to get most current
    // location values
    const { pathname, search } = location;

    if (
      Constants.UNSAFE_URL_CHARACTERS.test(pathname) ||
      Constants.UNSAFE_URL_CHARACTERS.test(search)
    ) {
      const normalizedPath = slugify({
        path: pathname,
        urlQuery: queryString.parse(search),
      });

      return this.props.push({
        pathname: normalizedPath.pathname,
        search: normalizedPath.query,
      });
    }
  };

  componentDidMount() {
    // load zendesk live chat widget on load
    // turned off
    // zendeskLiveChatWidgetHelper()

    this.props.activateGoogleOptimize();

    this.unlisten = this.props.history.listen((location) => {
      // load zendesk live chat widget on route
      // turned off
      // zendeskLiveChatWidgetHelper()

      const { pathname, search } = location;

      // If body has 'no_scroll' class on it, remove it
      togglePageScrollability();

      if (pathname !== this.props.appLocation.current) {
        this.props.activateGoogleOptimize();

        return this.props.signalRouteChange(pathname, search);
      }

      if (search !== this.props.appLocation.query) {
        return this.props.updateAppLocation({ query: search });
      }

      if (location.action === "POP") {
        // Back browser button
        return this.props.signalBackButtonPress(pathname, search);
      }
    });

    this._sanitizeUrl();

    if (window) {
      const { className } = getDeviceBrowser(
        window.navigator.userAgent,
        window.location.search
      );
      const newState = {
        deviceBrowserClass: className,
      };

      this.setState({ ...newState });
    }
  }

  componentWillUnmount = () => {
    this.unlisten();
  };

  componentDidUpdate = (prevProps) => {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this._sanitizeUrl(this.props.location);
    }
  };

  renderChildren = () => {
    if (!this.props.rehydrationComplete) {
      return <LoadingSpinner />;
    }

    return <Routes />;
  };

  renderCart = () => {
    if (isNewCartEnabled()) {
      return <Cart />;
    }

    return null;
  };

  render() {
    const className = classNames("app", this.state.deviceBrowserClass, {
      hide_ribbon: process.env.REACT_APP_HIDE_RIBBON === "true",
    });
    const hideRibbonFrom = __constructPathsFromEnvVars(
      process.env.REACT_APP_HIDE_RIBBON_PATHS
    );

    return (
      <TrackingProvider>
        {this.renderCart()}

        <div className={className}>
          <Ribbon customRibbon={true} otherPagesToHideFrom={hideRibbonFrom} />
          {/*<CountdownRibbon otherPagesToHideFrom={hideRibbonFrom}></CountdownRibbon>*/}
          {WHY_BUY_WHITELIST.includes(this.props.location.pathname) ? (
            <WhyToBuy />
          ) : null}
          {}
          {/*<Navigation hideNavigation={HIDE_NAV_FROM_PAGES.includes(this.props.location.pathname)}/>*/}
          <SuperNavigation></SuperNavigation>

          {this.renderChildren()}

          <Footer />
        </div>
      </TrackingProvider>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    errors: state.app.errors ? state.app.errors.app : null,
    rehydrationComplete: state.app.rehydrationComplete,
    appLocation: state.app.location || {},
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    signalRouteChange: (pathname, query) =>
      dispatch(signalRouteChange(pathname, query)),
    signalBackButtonPress: (pathname, query) =>
      dispatch(signalBackButtonPress(pathname, query)),
    push: (location) => dispatch(push(location)),
    updateAppLocation: ({ pathname, query }) =>
      dispatch(updateAppLocation({ pathname, query })),
    activateGoogleOptimize: () => dispatch(activateGoogleOptimize()),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
